/**
 * Canonical viewport breakpoints for the Pro app.
 *
 * WHY THIS EXISTS: most layout in this codebase is set through INLINE React
 * styles (`style={{ gridTemplateColumns: "1fr 1fr" }}`) — 31 of 64 components and
 * 21 of 26 screens. A CSS media query cannot override an inline style, so the
 * ≤600px foundation in pro.css / alethos.css stops at the component boundary:
 * the page frame reflows and the grids inside it do not. This hook is how inline
 * layout participates in the same breakpoints the stylesheets use.
 *
 * USE THE HOOK ONLY FOR INLINE STYLES. If a rule already lives in CSS, express it
 * as a media query there — that stays cheaper (no re-render) and keeps styling in
 * one place. The hook is the escape hatch for layout React owns, not a licence to
 * move styling into JS.
 *
 * Breakpoints are deliberately few. The stylesheets had accumulated eleven
 * different max-widths (360/440/560/680/700/720/760/880/1100/1180/1480), which is
 * how a design system stops being one. MOBILE is 600px to match the responsive
 * foundation already shipped.
 */
import { useEffect, useState } from "react";

export const BREAKPOINTS = {
  /** Phones. Single-column below this. */
  mobile: 600,
  /** Small tablets / split-screen. Two-column instead of three. */
  tablet: 900,
} as const;

function query(maxWidth: number): string {
  return `(max-width: ${maxWidth}px)`;
}

/**
 * Subscribes to a max-width media query. Returns false during SSR / before the
 * first effect, so the DESKTOP layout is the server-and-first-paint default —
 * chosen deliberately: a desktop grid briefly shown on a phone reflows on the
 * next tick, whereas defaulting to mobile would flash a single column on every
 * desktop load, which is far more visible.
 */
export function useMaxWidth(maxWidth: number): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query(maxWidth));
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    // addEventListener is not available on MediaQueryList in older Safari, which
    // still ships on iOS devices this app is meant to support.
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [maxWidth]);

  return matches;
}

/** True on phone-width viewports (≤600px). The one you usually want. */
export function useIsMobile(): boolean {
  return useMaxWidth(BREAKPOINTS.mobile);
}

/** True on tablet-width and below (≤900px). */
export function useIsTablet(): boolean {
  return useMaxWidth(BREAKPOINTS.tablet);
}

/**
 * Picks a grid-template-columns value for the current viewport.
 *
 *   gridTemplateColumns: responsiveCols(isMobile, "1fr 1fr", "1fr")
 *
 * Keeps the desktop value first so a reader sees the unchanged layout in the
 * position it always occupied, and the mobile override reads as the exception.
 */
export function responsiveCols(isMobile: boolean, desktop: string, mobile = "1fr"): string {
  return isMobile ? mobile : desktop;
}
